import { useCallback, useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../store/app/hooks"
import {
  setOpenAlert,
  setLoadingDelete,
  setCloseAlert,
} from "../../../store/features/alert/alertSlice"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import {
  selectMaterialTypePagination,
  selectMaterialTypeQuery,
  setMaterialTypePagination,
  setMaterialTypeQuery,
} from "../../../store/features/materialType/materialTypeSlice"
import { graphql } from "../../../gql"
import CustomTable from "../../Common/CustomTable/CustomTable"
import Loader from "../../Common/Loader/Loader"
import { getErrorsAsString } from "../../../utils/getErrorsAsString"
import {
  MaterialType,
  MaterialTypeWhereInput,
  QueryMode,
  SortOrder,
} from "../../../gql/graphql"
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "material-react-table"
import { Pagination } from "../../../types/Pagination"
import { setOpenSnackbar } from "../../../store/features/snackbar/snackbarSlice"
import { updateCacheMaterialType } from "../../../caches/updateCacheMaterialType"
import { useNavigate } from "react-router-dom"
import { setTitle } from "../../../store/features/menu/menuSlice"
import { havePermissions, whereInputClause } from "../../../utils/permissions"
import { getUserCountryId } from "../../../utils/authToken"
import useLang from "../../../hooks/useLang"

export const LIST_MATERIALTYPES = graphql(`
  query MaterialTypes(
    $where: MaterialTypeWhereInput
    $orderBy: [MaterialTypeOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
  ) {
    materialTypes(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      name_fr
      name_en
      Category {
        name_fr
        id
        name_en
      }
    }
    aggregateMaterialType {
      _count {
        _all
      }
    }
  }
`)

export const DELETE_MATERIALTYPE = graphql(`
  mutation DeleteOneMaterialType($where: MaterialTypeWhereUniqueInput!) {
    deleteOneMaterialType(where: $where) {
      id
      name_fr
      name_en
    }
  }
`)

const LIST_FILTER = graphql(`
  query filtersListItems {
    categories {
      id
      name_fr
    }
  }
`)

const ListMaterialType = () => {
  const { page, size } = useAppSelector(selectMaterialTypePagination)
  const query = useAppSelector(selectMaterialTypeQuery)
  const dispatch = useAppDispatch()
  const countryId = getUserCountryId()
  const { getText } = useLang()
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { loading: loadingFilter, data: dataFilter } = useQuery(LIST_FILTER)

  const navigate = useNavigate()

  const getValueFilter = (el: { name_fr: string; id: number }) => {
    return {
      label: el.name_fr,
      value: el.id.toString(),
      text: el.name_fr,
    }
  }

  const getFilters: () => MaterialTypeWhereInput = useCallback(() => {
    if (columnFilters.length) {
      return columnFilters.reduce(
        (
          accumulator: MaterialTypeWhereInput,
          { id, value }: { id: string; value: any },
        ) => {
          switch (id) {
            case "Category":
              accumulator = {
                ...accumulator,
                Category: {
                  is: {
                    id: {
                      equals: Number(value),
                    },
                  },
                },
              }
              break
            case "name_fr":
              accumulator = {
                ...accumulator,
                OR: [
                  {
                    name_fr: {
                      search: value,
                    },
                  },
                  {
                    name_fr: {
                      contains: value,
                    },
                  },
                ],
              }
              break
            case "id":
              accumulator = {
                ...accumulator,
                id: {
                  equals: Number(value),
                },
              }
              break
            case "name_en":
              accumulator = {
                ...accumulator,
                OR: [
                  {
                    name_en: {
                      search: value,
                    },
                  },
                  {
                    name_en: {
                      contains: value,
                    },
                  },
                ],
              }
              break
          }
          return accumulator
        },
        {},
      )
    }
    return {}
  }, [columnFilters])

  const queryName = useCallback(() => {
    return query
      ? {
          OR: [
            { name_fr: { contains: query, mode: QueryMode.Insensitive } },
            { name_en: { contains: query, mode: QueryMode.Insensitive } },
          ],
        }
      : {}
  }, [query])

  const [getMaterials, { loading, data }] = useLazyQuery(LIST_MATERIALTYPES)

  const whereCluase = whereInputClause({
    Countries: {
      some: {
        id: {
          equals: countryId,
        },
      },
    },
  })

  useEffect(() => {
    getMaterials({
      variables: {
        take: size,
        skip: page * size,
        orderBy: { name_fr: SortOrder.Asc },
        where: {
          ...whereCluase,
          ...queryName(),
          ...getFilters(),
        },
      },
    })
  }, [getMaterials, page, query, queryName, size, getFilters])

  const [deleteMaterialType] = useMutation(DELETE_MATERIALTYPE)

  const columns = useMemo<MRT_ColumnDef<MaterialType>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableEditing: false,
        size: 80,
        enableClickToCopy: true,
        filterVariant: "text",
        id: "id",
      },
      {
        accessorKey: "name_fr",
        header: getText("Nom en fr"),
        enableClickToCopy: true,
        filterVariant: "text",
        id: "name_fr",
      },
      {
        accessorKey: "name_en",
        header: getText("Nom en en"),
        enableClickToCopy: true,
        filterVariant: "text",
        id: "name_en",
      },
      {
        accessorKey: "Category.name_fr",
        header: getText("Catégorie"),
        enableClickToCopy: true,
        filterVariant: "select",
        filterSelectOptions: dataFilter?.categories?.map((el) =>
          getValueFilter(el),
        ),
        id: "Category",
      },
    ],
    [dataFilter, getText],
  )

  const handleChangePagination = (pagination: Pagination) =>
    dispatch(setMaterialTypePagination(pagination))

  const handleValidDelete = (id: number) => {
    dispatch(setLoadingDelete(true))
    deleteMaterialType({
      variables: {
        where: {
          id,
        },
      },
      onCompleted: () => {
        dispatch(
          setOpenSnackbar({
            message: getText("Le type matière a été supprimé avec succès"),
            status: "success",
          }),
        )
        dispatch(setLoadingDelete(false))
        dispatch(setCloseAlert())
      },
      onError: (err) => {
        const message = getErrorsAsString(err)
        dispatch(setCloseAlert())
        dispatch(setOpenSnackbar({ message }))
      },
      update: (cache, { data }) => {
        updateCacheMaterialType({
          action: "delete",
          cache,
          entryData: data?.deleteOneMaterialType,
        })
      },
    })
  }

  const handleOpenDeleteDialog = (id: number) => {
    dispatch(
      setOpenAlert({
        handleValid: () => handleValidDelete(id),
        message: getText(
          "Êtes-vous vraiment sûr de vouloir supprimer ce type matière ?",
        ),
        isLoading: false,
      }),
    )
  }

  const onChangeSearchValue = (value: string) =>
    dispatch(setMaterialTypeQuery(value))

  const handleEdit = (id: number) => {
    dispatch(setTitle(getText("Modifier un type matière")))
    navigate(`/material-type/edit/${id}`)
  }

  const handleView = (id: number) => {
    dispatch(setTitle(getText("Detail d'un type matière")))
    navigate(`/material-type/detail/${id}`)
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  if (loadingFilter) return <Loader />

  return (
    <CustomTable
      enableColumnFilters
      columns={columns}
      data={data?.materialTypes || []}
      lableAddNew={getText("Créer un nouveau type matière")}
      rootLisName={"/material-type/add"}
      isLoading={loading}
      showProgressBars={loading}
      rowCount={data?.aggregateMaterialType._count?._all || 0}
      handleChangePagination={handleChangePagination}
      pageIndex={page}
      pageSize={size}
      searchValue={query}
      onChangeSearchValue={onChangeSearchValue}
      handleDelete={
        havePermissions(["materialType.delete"])
          ? handleOpenDeleteDialog
          : undefined
      }
      isHaveHeaderAction={havePermissions(["materialType.create"])}
      handleEdit={
        havePermissions(["materialType.change"]) ? handleEdit : undefined
      }
      isHaveView={havePermissions(["materialType.view"])}
      handleView={
        havePermissions(["materialType.view"]) ? handleView : undefined
      }
      isHaveAction={havePermissions([
        "materialType.view",
        "materialType.change",
        "materialType.delete",
      ])}
      handleRefresh={handleRefresh}
      columnFilters={columnFilters}
      setColumnFilters={setColumnFilters}
    />
  )
}

export default ListMaterialType
